import { useEffect } from 'react'
import { Button, Popover } from '@mui/material'
import { useFeatureFlagContext } from '@/feature_flags/FeatureFlagProvider'
import useOverlay from '@/hooks/useOverlay'
import useViewerId from '@/hooks/useViewerId'
import { useGetDocumentApprovalState } from '@/service-library/hooks/document-approvals'
import ApprovalsNeededCard, { getSummary } from './ApprovalsNeededCard'

type ApprovalsButtonProps = {
  documentId: string
  onMoveToNextDocument?: () => void
}

export default function ApprovalsButton({
  documentId,
  onMoveToNextDocument,
}: ApprovalsButtonProps) {
  const { show_approvals_v2: showApprovalsV2 } = useFeatureFlagContext()
  const overlay = useOverlay()
  const viewerId = useViewerId()
  const { approvalState, isError } = useGetDocumentApprovalState({
    id: documentId,
    enabled: !!showApprovalsV2,
  })

  const isHidden =
    !showApprovalsV2 || isError || !approvalState?.policies.length

  // Hiding the button removes its popover from the page, but the popover's
  // open flag from useOverlay lives here and stays on. So close it too.
  // Otherwise the popover opens by itself when the button comes back, stuck
  // to the removed button at the top-left of the page.
  const closeOverlay = overlay.close
  useEffect(() => {
    if (isHidden) closeOverlay()
  }, [isHidden, closeOverlay])

  if (isHidden) {
    return null
  }

  const { badge } = getSummary(approvalState, viewerId)
  const { Icon } = badge

  return (
    <>
      <Button
        color={badge.tone === 'neutral' ? 'inherit' : badge.tone}
        variant="text"
        startIcon={<Icon />}
        onClick={overlay.open}
        sx={{ flexShrink: 0 }}
      >
        Approvals
      </Button>

      <Popover
        open={overlay.isOpen}
        anchorEl={overlay.anchorEl as Element}
        onClose={overlay.close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disableScrollLock
        disableRestoreFocus
        slotProps={{ paper: { sx: { width: 420 } } }}
      >
        <ApprovalsNeededCard
          documentId={documentId}
          collapsible
          showMoreStorageKey="approvals-popover-show-more"
          onMoveToNextDocument={onMoveToNextDocument}
          elevation={0}
        />
      </Popover>
    </>
  )
}
